import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 24);
    }
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const sectionLinks = [
    { label: t("nav.spreadsheet"), to: "/#what-is" },
    { label: t("nav.guides"), to: "/#guides" },
    { label: t("nav.comparisons"), to: "/#comparisons" },
    { label: t("nav.categories"), to: "/#categories" },
    { label: t("nav.safety"), to: "/#safety" },
    { label: t("faq.title"), to: "/#faq" },
  ];

  const overHero = location.pathname === "/" && !scrolled;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled || location.pathname !== "/"
          ? "bg-background-50/95 backdrop-blur border-b border-background-200"
          : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-4 md:px-6 py-3.5">
        <Link
          to="/"
          className="flex items-center gap-2.5 cursor-pointer"
          onClick={() => setMenuOpen(false)}
        >
          <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-file-list-3-line text-lg"></i>
          </span>
          <span
            className={`font-heading text-lg font-bold leading-tight ${
              overHero ? "text-background-50" : "text-foreground-950"
            }`}
          >
            USFans{" "}
            <span
              className={`font-medium ${
                overHero ? "text-background-100" : "text-foreground-600"
              }`}
            >
              Spreadsheet
            </span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-0.5">
          <Link
            to="/"
            className={`rounded-md px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
              overHero
                ? "text-background-50 hover:bg-background-50/15"
                : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
            }`}
          >
            {t("nav.home")}
          </Link>
          {sectionLinks.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`rounded-md px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                overHero
                  ? "text-background-50 hover:bg-background-50/15"
                  : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <LanguageSwitcher light={overHero} />
          <Link
            to="/buying-guide"
            className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap cursor-pointer"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-shopping-cart-2-line"></i>
            </span>
            {t("nav.buyingGuide")}
          </Link>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-lg border cursor-pointer ${
              overHero
                ? "border-background-50/40 text-background-50"
                : "border-foreground-200 text-foreground-800"
            }`}
            aria-label="Menu"
          >
            <i
              className={`text-xl ${
                menuOpen ? "ri-close-line" : "ri-menu-line"
              }`}
            ></i>
          </button>
        </div>
      </nav>

      {menuOpen && (
        <div className="lg:hidden border-t border-background-200 bg-background-50 px-4 py-3 max-h-[80vh] overflow-y-auto">
          <Link
            to="/"
            onClick={() => setMenuOpen(false)}
            className="block rounded-md px-3 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-background-100"
          >
            {t("nav.home")}
          </Link>
          <Link
            to="/buying-guide"
            onClick={() => setMenuOpen(false)}
            className="mt-1 flex items-center gap-2 rounded-md bg-primary-500 px-3 py-2.5 text-sm font-semibold text-background-50"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-shopping-cart-2-line"></i>
            </span>
            {t("nav.buyingGuide")}
          </Link>

          <div className="mt-3 border-t border-background-200 pt-2">
            <p className="px-3 py-1 text-xs font-semibold uppercase tracking-wide text-foreground-500">
              {t("nav.guides")}
            </p>
            {sectionLinks.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setMenuOpen(false)}
                className="block rounded-md px-3 py-2 text-sm font-medium text-foreground-800 hover:bg-background-100"
              >
                {item.label}
              </Link>
            ))}
          </div>

          <div className="mt-3 border-t border-background-200 pt-2">
            <p className="px-3 py-1 text-xs font-semibold uppercase tracking-wide text-foreground-500">
              {t("nav.categories")}
            </p>
            <div className="grid grid-cols-2 gap-1">
              {categories.map((cat) => (
                <a
                  key={cat.key}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center justify-between rounded-md px-3 py-2 text-sm text-foreground-700 hover:bg-background-100"
                >
                  <span className="whitespace-nowrap overflow-hidden text-ellipsis">
                    {t(`category.${cat.key}`)}
                  </span>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}